// Additional components for Omatech Digital
import React, { useState, useEffect, useRef, useCallback } from 'react';
import analytics from './analytics';

// Helper functions and constants
const uid = () => Math.random().toString(36).slice(2,10);
const fmt = (ts) => new Date(ts).toLocaleString();
const PROJECT_TYPES = ["Web Development","Web App Development","Google Ads","Keyword Research","SEO","Landing Page","Other"];
const STATUSES = ["Submitted","In Review","Quoted","In Progress","Completed"];
const API_BASE = process.env.REACT_APP_API_BASE || '';

export function CaseStudy(){
  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-8">
        <div className="rounded-2xl bg-white border shadow p-6">
          <div className="text-sm text-gray-500 mb-2">Case Study</div>
          <h3 className="text-2xl font-semibold mb-2">D2C Brand cut CPA by 41% in 30 days</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Built fast landing pages and event tracking</li>
            <li>• Restructured Google Ads with SKAGs + negative map</li>
            <li>• Keyword intent layering + responsive search ads</li>
          </ul>
          <div className="mt-4 text-sm text-gray-500">Result: +62% conv., −41% CPA</div>
        </div>
        <div className="rounded-2xl text-white p-6" style={{backgroundImage:"linear-gradient(to right,#4f46e5,#06b6d4)"}}>
          <div className="text-sm opacity-90">Get a free audit</div>
          <div className="text-2xl font-semibold">Show us your funnel — we'll show you the leaks</div>
          <button className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-gray-900" onClick={()=>window.scrollTo({top:0,behavior:'smooth'})}>Start now →</button>
        </div>
      </div>
    </section>
  );
}

export function Testimonials(){
  const items=[
    {
      n:"Kriti Sharma",
      role:"CEO & Founder",
      company:"TechFlow Solutions",
      t:"Omatech Digital transformed our business completely. Their ROI-focused approach doubled our demo requests within 3 months. The team's expertise in data-driven marketing is exceptional!",
      rating:5,
      image:"👩‍💼",
      results:["+200% Demo Requests", "3.2x ROI", "45% Cost Reduction"],
      industry:"SaaS B2B"
    },
    {
      n:"Aman Patel",
      role:"Marketing Director", 
      company:"BeautyGlow D2C",
      t:"Incredible results! Our landing pages now load in milliseconds and our ad spend efficiency improved by 300%. Omatech's technical expertise and strategic thinking are world-class.",
      rating:5,
      image:"👨‍💻",
      results:["300% Ad Efficiency", "2.1s Load Time", "67% Conversion Boost"],
      industry:"D2C Beauty"
    },
    {
      n:"Dr. Riya Verma",
      role:"Owner & Director",
      company:"Wellness Clinic",
      t:"Local SEO and Google Ads expertise at its finest! Our appointment calendar is now fully booked. The team's attention to detail and patient-focused approach exceeded all expectations.",
      rating:5,
      image:"👩‍⚕️",
      results:["100% Booked Calendar", "2.3x CTR", "#1 Local Ranking"],
      industry:"Healthcare"
    },
    {
      n:"Rajesh Kumar",
      role:"Operations Head",
      company:"EcoTech Manufacturing",
      t:"Omatech's technical SEO work was game-changing. We went from page 5 to page 1 in 90 days, and our organic traffic increased by 400%. Outstanding results!",
      rating:5,
      image:"🏭",
      results:["400% Traffic Growth", "Page 1 Rankings", "89% Lead Increase"],
      industry:"Manufacturing"
    },
    {
      n:"Priya Singh",
      role:"Digital Marketing Manager",
      company:"FinServe Pro",
      t:"The keyword research and Google Ads strategy delivered exactly what we needed. Our CPA dropped by 52% while maintaining the same quality leads. Highly recommended!",
      rating:5,
      image:"💼",
      results:["52% CPA Reduction", "2.8x ROAS", "Quality Lead Growth"],
      industry:"Financial Services"
    }
  ];
  
  const StarRating = ({ rating }) => (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <span key={i} className="text-yellow-400 text-lg">
          {i < rating ? "★" : "☆"}
        </span>
      ))}
    </div>
  );

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white border-t">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
            Client Success Stories
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Trusted by 500+ businesses across India. Here's what our clients say about their transformation journey.
          </p>
        </div>
        
        {/* Marquee: Client Success Stories in one line */}
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div 
            className="flex items-center gap-8 py-6 will-change-transform"
            style={{
              width: '200%',
              animation: 'marquee 30s linear infinite'
            }}
          >
            {/* Sequence A */}
            {items.map((x,i)=> (
              <div key={`A-${i}`} className="flex items-center gap-3 whitespace-nowrap px-4">
                <span className="text-xl">{x.image}</span>
                <span className="text-sm font-semibold text-gray-900">{x.company}</span>
                <span className="text-xs text-gray-500">• {x.industry}</span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-sm text-gray-700 italic max-w-[420px] truncate">"{x.t}"</span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-yellow-500 text-sm">{Array.from({length:5}).map((_,ri)=> ri < x.rating ? '★' : '☆').join('')}</span>
              </div>
            ))}
            {/* Sequence B (duplicate for seamless loop) */}
            {items.map((x,i)=> (
              <div key={`B-${i}`} className="flex items-center gap-3 whitespace-nowrap px-4">
                <span className="text-xl">{x.image}</span>
                <span className="text-sm font-semibold text-gray-900">{x.company}</span>
                <span className="text-xs text-gray-500">• {x.industry}</span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-sm text-gray-700 italic max-w-[420px] truncate">"{x.t}"</span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-yellow-500 text-sm">{Array.from({length:5}).map((_,ri)=> ri < x.rating ? '★' : '☆').join('')}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* (Optional) Keep the stats band below */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-8 bg-white rounded-2xl px-8 py-4 shadow-lg border">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">500+</div>
              <div className="text-sm text-gray-600">Happy Clients</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-600">98%</div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">4.9★</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FAQ(){
  const items=[
    {q:"How fast can we start?",a:"Usually within 3–5 business days after the discovery call."},
    {q:"Do you manage ad creatives?",a:"Yes — copy, variants, and landing pages with A/B tests."},
    {q:"What if I only need keywords?",a:"We deliver curated lists with match types + intent mapping."},
    {q:"Do you offer monthly retainers?",a:"Yes, Growth & Performance tiers include monthly management."},
    {q:"What industries do you specialize in?",a:"We work across all industries including SaaS, D2C, Healthcare, Manufacturing, and Financial Services."},
    {q:"How do you measure success?",a:"We track KPIs like ROI, conversion rates, traffic growth, and lead quality to ensure measurable results."},
    {q:"Do you provide ongoing support?",a:"Yes, our Growth and Performance packages include monthly management and optimization."},
    {q:"What makes Omatech Digital different?",a:"Our data-driven approach, technical expertise, and proven track record of delivering ROI-focused results."},
  ];
  
  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about working with Omatech Digital
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {items.map((it,i)=>(
            <div key={i} className="group p-6 rounded-2xl bg-white border shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="font-semibold text-gray-800 mb-2">{it.q}</div>
              <div className="text-sm text-gray-600 leading-relaxed">{it.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// New Trust & Social Proof Section
export function TrustSection(){
  const stats = [
    { number: "500+", label: "Happy Clients", icon: "🎯" },
    { number: "98%", label: "Success Rate", icon: "📈" },
    { number: "4.9★", label: "Average Rating", icon: "⭐" },
    { number: "₹2.5Cr+", label: "Revenue Generated", icon: "💰" },
    { number: "150+", label: "Projects Completed", icon: "🚀" },
    { number: "24/7", label: "Support Available", icon: "🔄" }
  ];
  
  const certifications = [
    { name: "Google Ads Certified", icon: "🔍", color: "from-blue-500 to-blue-600" },
    { name: "Google Analytics Expert", icon: "📊", color: "from-green-500 to-green-600" },
    { name: "SEO Specialist", icon: "🎯", color: "from-purple-500 to-purple-600" },
    { name: "Conversion Expert", icon: "⚡", color: "from-orange-500 to-orange-600" }
  ];
  
  const clients = [
    "TechFlow Solutions", "BeautyGlow D2C", "Wellness Clinic", 
    "EcoTech Manufacturing", "FinServe Pro", "SaaS CRM Platform"
  ];
  
  return (
    <section className="py-16 bg-white border-t">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
            Trusted by Industry Leaders
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join hundreds of successful businesses that have transformed their digital presence with Omatech Digital
          </p>
        </div>
        
        {/* Statistics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center group">
              <div className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>
              <div className="text-2xl font-bold text-gray-800 mb-1">{stat.number}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
        
        {/* Certifications */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">Our Certifications & Expertise</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {certifications.map((cert, idx) => (
              <div key={idx} className="text-center group">
                <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${cert.color} text-white grid place-items-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {cert.icon}
                </div>
                <div className="font-semibold text-gray-800">{cert.name}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Client Logos */}
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-8 text-gray-800">Some of Our Valued Clients</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {clients.map((client, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-300">
                <div className="text-lg font-semibold text-gray-700">{client}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CTA({setRoute}){
  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="rounded-2xl p-8 text-white flex flex-col md:flex-row items-center justify-between" style={{backgroundImage:"linear-gradient(to right,#8B00FF,#7350FF,#12C7FF)"}}>
          <div className="mb-4 md:mb-0">
            <div className="text-2xl font-semibold">Ready to scale?</div>
            <div className="opacity-90">Share your requirements — get a practical action plan.</div>
          </div>
          <div className="flex gap-3">
            <button onClick={()=>setRoute("register")} className="px-5 py-3 rounded-xl bg-white text-gray-900">Create account</button>
            <button onClick={()=>setRoute("newreq")} className="px-5 py-3 rounded-xl bg-white/10 border border-white/40">Submit requirements</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Case(){
  const items=[
    {
      title:"D2C Skincare Brand",
      industry:"Beauty & Personal Care",
      duration:"3 months",
      kpis:["+62% conversions","-41% CPA","+28% ROAS"],
      bullets:["High-speed landing pages","SKAGs + negative keywords","Responsive search ads with intent layers"],
      image:"🧴",
      challenge:"Low conversion rates and high ad spend waste",
      solution:"Complete funnel optimization with A/B testing",
      results:"Tripled monthly revenue while reducing costs"
    },
    {
      title:"Local Healthcare Clinic",
      industry:"Healthcare",
      duration:"2 months", 
      kpis:["+140 bookings/mo","+2.3x CTR","#1 maps pack"],
      bullets:["Local SEO optimization","Call tracking setup","Appointment funnel design"],
      image:"🏥",
      challenge:"Poor local visibility and low appointment bookings",
      solution:"Comprehensive local SEO + Google Ads strategy",
      results:"Fully booked calendar within 60 days"
    },
    {
      title:"SaaS CRM Platform",
      industry:"B2B Technology",
      duration:"4 months",
      kpis:["+78% demo requests","-34% CAC","+19% MQL→SQL"],
      bullets:["Feature-focused landing pages","Query intent mapping","Advanced retargeting campaigns"],
      image:"💻",
      challenge:"High customer acquisition costs and low demo conversions",
      solution:"Intent-based marketing with conversion optimization",
      results:"Doubled qualified leads while reducing CAC by 34%"
    },
    {
      title:"E-commerce Fashion",
      industry:"Fashion Retail",
      duration:"3 months",
      kpis:["+89% revenue","+156% ROAS","-23% cart abandonment"],
      bullets:["Mobile-first design","Conversion rate optimization","Email marketing automation"],
      image:"👗",
      challenge:"Poor mobile experience and high cart abandonment",
      solution:"Complete mobile optimization with CRO",
      results:"Mobile revenue increased by 300%"
    },
    {
      title:"Manufacturing Company",
      industry:"Industrial",
      duration:"5 months",
      kpis:["+400% organic traffic","Page 1 rankings","+89% lead increase"],
      bullets:["Technical SEO audit","Content strategy","Link building campaign"],
      image:"🏭",
      challenge:"No online presence and zero organic leads",
      solution:"Complete SEO overhaul with content marketing",
      results:"First organic leads within 90 days"
    },
    {
      title:"Financial Services",
      industry:"Finance",
      duration:"3 months",
      kpis:["+52% CPA reduction","2.8x ROAS","Quality lead growth"],
      bullets:["Keyword research","Google Ads optimization","Landing page design"],
      image:"💼",
      challenge:"High cost per acquisition and poor lead quality",
      solution:"Precise targeting with conversion optimization",
      results:"Reduced CPA by 52% while improving lead quality"
    }
  ];
  
  return (
    <main className="max-w-7xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
          Our Portfolio & Case Studies
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Real results from real clients across diverse industries. Each project demonstrates our data-driven approach to digital marketing success.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((c,i)=> (
          <div key={i} className="group rounded-3xl bg-white border shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
            {/* Header with image and industry */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-3xl">{c.image}</div>
                <div className="text-xs px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                  {c.industry}
                </div>
              </div>
              
              <div className="font-bold text-xl text-gray-800 mb-2">{c.title}</div>
              <div className="text-sm text-gray-500 mb-3">Duration: {c.duration}</div>
              
              {/* Challenge & Solution */}
              <div className="space-y-3 mb-4">
                <div>
                  <div className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Challenge</div>
                  <div className="text-sm text-gray-700">{c.challenge}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#7350FF] uppercase tracking-wide mb-1">Solution</div>
                  <div className="text-sm text-gray-700">{c.solution}</div>
                </div>
              </div>
            </div>
            
            {/* Results section */}
            <div className="px-6 pb-6">
              <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-3">Results</div>
              <div className="text-sm text-gray-700 mb-4">{c.results}</div>
              
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {c.kpis.map((k,j)=>(<div key={j} className="rounded-lg border bg-gray-50 px-2 py-2 text-xs text-center font-medium text-gray-700">{k}</div>))}
              </div>
              
              {/* Key features */}
              <div className="space-y-2">
                {c.bullets.map((b,j)=>(<div key={j} className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                  {b}
                </div>))}
              </div>
            </div>
            
            {/* Hover overlay */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        ))}
      </div>
      
      {/* Call to action */}
      <div className="text-center mt-12">
        <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white font-medium hover:from-indigo-700 hover:to-cyan-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
          <span>Ready to join our success stories?</span>
          <span className="text-xl">🚀</span>
        </div>
      </div>
    </main>
  );
}

export function Blog(){
  const [q,setQ]=useState("");
  const [open,setOpen]=useState(null);
  const [selectedCategory,setSelectedCategory]=useState("All");
  
  const posts=[
    {
      id:1,
      title:"Google Ads: SKAGs vs. Thematic Clustering",
      excerpt:"Master the art of keyword organization with proven strategies for maximum ROI and campaign efficiency.",
      body:"Single Keyword Ad Groups (SKAGs) shine for high-intent, exact queries where you want precise control over ad copy and landing pages. For discovery or broad funnels, thematic clusters with strong negative keywords keep reach without bleeding budget. The key is understanding your funnel stage and user intent. SKAGs work best for bottom-funnel, high-converting keywords, while thematic clusters excel at top-funnel awareness and consideration stages. Always start with SKAGs for your highest-value keywords, then expand to thematic for broader reach.",
      tags:["Google Ads","CRO","PPC"],
      category:"Google Ads",
      author:"Omatech Team",
      date:"2024-12-15",
      readTime:"5 min read",
      featured:true,
      image:"📊"
    },
    {
      id:2,
      title:"SEO Technical Checklist for New Websites",
      excerpt:"Avoid the 10 most common technical SEO pitfalls that can cripple your search rankings from day one.",
      body:"From canonical tags to XML sitemaps, Core Web Vitals and hreflang—ship this comprehensive checklist with every launch to prevent slow indexation and poor search visibility. Technical SEO is the foundation that everything else builds upon. Start with server performance, implement proper redirects, ensure mobile-first indexing, and optimize for Core Web Vitals. Remember, Google's algorithms are increasingly sophisticated, so technical excellence is no longer optional—it's essential for competitive rankings.",
      tags:["SEO","Web Development","Technical"],
      category:"SEO",
      author:"Omatech Team",
      date:"2024-12-10",
      readTime:"8 min read",
      featured:false,
      image:"🔧"
    },
    {
      id:3,
      title:"Landing Page Conversion Patterns That Work",
      excerpt:"Hero, proof, CTA—then repeat. The proven formula for high-converting landing pages across all industries.",
      body:"Use repeatable blocks: problem → proof → offer → CTA. Add social proof and objection handling within viewport one and three. The key to conversion is understanding user psychology and creating a clear path to action. Every element should serve a purpose, from compelling headlines to strategic CTAs. A/B test everything, but start with the fundamentals: clear value proposition, social proof, and frictionless conversion process.",
      tags:["CRO","Design","Landing Pages"],
      category:"CRO",
      author:"Omatech Team",
      date:"2024-12-05",
      readTime:"6 min read",
      featured:false,
      image:"🎯"
    },
    {
      id:4,
      title:"Keyword Research: Beyond Search Volume",
      excerpt:"Discover how to identify high-value keywords that your competitors are missing for maximum organic growth.",
      body:"Keyword research isn't just about search volume—it's about understanding user intent, competitive landscape, and business value. Look for long-tail opportunities, question-based queries, and emerging trends. Use tools like Google Trends, Answer the Public, and competitor analysis to find gaps in the market. The best keywords often have lower search volume but higher conversion potential and less competition.",
      tags:["SEO","Keyword Research","Strategy"],
      category:"SEO",
      author:"Omatech Team",
      date:"2024-11-28",
      readTime:"7 min read",
      featured:false,
      image:"🔍"
    },
    {
      id:5,
      title:"Mobile-First Design: Why It's Critical for SEO",
      excerpt:"Google's mobile-first indexing means your mobile experience directly impacts your search rankings.",
      body:"Mobile-first design isn't just a trend—it's Google's primary indexing method. Your mobile site's performance, usability, and content quality directly affect your search rankings. Focus on Core Web Vitals, touch-friendly navigation, and fast loading times. Remember, mobile users have different behaviors and needs than desktop users, so design for their context first.",
      tags:["Web Development","Mobile","SEO"],
      category:"Web Development",
      author:"Omatech Team",
      date:"2024-11-20",
      readTime:"4 min read",
      featured:false,
      image:"📱"
    },
    {
      id:6,
      title:"Email Marketing Automation for Lead Nurturing",
      excerpt:"Build relationships with prospects through strategic email sequences that convert subscribers into customers.",
      body:"Email automation is the backbone of modern lead nurturing. Create personalized sequences based on user behavior, segment your audience, and deliver value at every touchpoint. The key is providing relevant content that moves prospects through your funnel. Use behavioral triggers, A/B testing, and analytics to optimize your campaigns for maximum engagement and conversion.",
      tags:["Email Marketing","Automation","Lead Generation"],
      category:"Marketing",
      author:"Omatech Team",
      date:"2024-11-15",
      readTime:"9 min read",
      featured:false,
      image:"📧"
    }
  ];
  
  const categories = ["All", "Google Ads", "SEO", "CRO", "Web Development", "Marketing"];
  const filtered = posts.filter(p => {
    const matchesSearch = [p.title, p.excerpt, p.body, ...p.tags].join(" ").toLowerCase().includes(q.toLowerCase());
    const matchesCategory = selectedCategory === "All" || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  return (
    <main className="max-w-7xl mx-auto px-4 py-16">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">
          Digital Marketing Insights
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Expert insights, strategies, and tips to help your business grow online. 
          Stay ahead with the latest digital marketing trends and best practices.
        </p>
      </div>
      
      {/* Search and Filter Section */}
      <div className="mb-8 space-y-4">
        {/* Search Bar */}
        <div className="relative max-w-2xl mx-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400">🔍</span>
          </div>
          <input 
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg"
            placeholder="Search articles, keywords, or topics..." 
            value={q} 
            onChange={e => setQ(e.target.value)} 
          />
        </div>
        
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>
      
      {/* Featured Post */}
      {filtered.length > 0 && filtered.some(p => p.featured) && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Featured Article</h2>
          {filtered.filter(p => p.featured).map(featuredPost => (
            <div key={featuredPost.id} className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-50 to-cyan-50 border border-indigo-100 p-8 hover:shadow-2xl transition-all duration-500">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{featuredPost.image}</span>
                    <div className="text-sm text-indigo-600 font-medium">{featuredPost.category}</div>
                    <div className="text-sm text-gray-500">• {featuredPost.readTime}</div>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4 group-hover:text-indigo-600 transition-colors duration-300">
                    {featuredPost.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-sm text-gray-500">By {featuredPost.author}</div>
                    <div className="text-sm text-gray-500">• {formatDate(featuredPost.date)}</div>
                  </div>
                  <button 
                    onClick={() => setOpen(featuredPost)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transform hover:scale-105 transition-all duration-300 font-medium"
                  >
                    Read Full Article
                    <span className="text-lg">→</span>
                  </button>
                </div>
                <div className="text-center">
                  <div className="text-8xl opacity-20 group-hover:opacity-30 transition-opacity duration-300">
                    {featuredPost.image}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Blog Posts Grid */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          Latest Articles {selectedCategory !== "All" && `in ${selectedCategory}`}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.filter(p => !p.featured).map(post => (
            <article key={post.id} className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
              {/* Post Header */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{post.image}</span>
                  <div className="text-xs px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                    {post.category}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-indigo-600 transition-colors duration-300 line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                  {post.excerpt}
                </p>
                
                {/* Post Meta */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>👤 {post.author}</span>
                    <span>•</span>
                    <span>📅 {formatDate(post.date)}</span>
                  </div>
                  <div className="text-sm text-gray-500">{post.readTime}</div>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.map(tag => (
                    <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                
                {/* Read More Button */}
                <button 
                  onClick={() => setOpen(post)}
                  className="w-full py-2 px-4 border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-300 font-medium group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600"
                >
                  Read Article
                </button>
              </div>
            </article>
          ))}
        </div>
        
        {/* No Results Message */}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No articles found</h3>
            <p className="text-gray-600">Try adjusting your search terms or category filter.</p>
          </div>
          )}
      </div>
      
      {/* Newsletter Signup */}
      <div className="text-center py-12 bg-gradient-to-r from-indigo-50 to-cyan-50 rounded-3xl border border-indigo-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-3">Stay Updated</h3>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Get the latest digital marketing insights, tips, and strategies delivered to your inbox.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input 
            type="email" 
            placeholder="Enter your email" 
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transform hover:scale-105 transition-all duration-300 font-medium">
            Subscribe
          </button>
        </div>
      </div>
      
      {/* Article Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center p-4 z-50" onClick={() => setOpen(null)}>
          <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-8">
              {/* Article Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{open.image}</span>
                  <div className="text-sm px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                    {open.category}
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">{open.title}</h2>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span>By {open.author}</span>
                  <span>•</span>
                  <span>{formatDate(open.date)}</span>
                  <span>•</span>
                  <span>{open.readTime}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {open.tags.map(tag => (
                    <span key={tag} className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">{open.body}</p>
              </div>
              
              {/* Article Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Share this article with your team
                  </div>
                  <button 
                    onClick={() => setOpen(null)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export function Contact(){
  const formRef = useRef(null);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  
  const clearForm = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
  };
  
  const submit = async () => {
    setOk(""); 
    setErr("");
    
    if (!formRef.current) return;
    
    const formData = new FormData(formRef.current);
    const name = formData.get('name');
    const email = formData.get('email');
    const phone = formData.get('phone');
    const subject = formData.get('subject');
    const message = formData.get('message');
    
    if(!name||!email||!message) return setErr("Name, Email, and Message are required.");
    
    const contactData = {name, email, phone, subject, message, id: Date.now()};
    const recs=JSON.parse(localStorage.getItem("om_contacts")||"[]");
    recs.unshift(contactData);
    localStorage.setItem("om_contacts", JSON.stringify(recs));
    
          // Send admin notification for contact form submission
      try {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (!isLocalhost) {
        const adminResponse = await fetch('/.netlify/functions/admin-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationType: 'form_submission',
          userData: {
            type: 'Contact Form',
            userEmail: email,
            userName: name,
            userPhone: phone || 'Not provided',
            subject: subject || 'Not provided',
            message: message,
            submissionTime: new Date().toLocaleString()
          }
        })
      });
        
        const contentType = adminResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const adminResult = await adminResponse.json();
          if (adminResponse.ok && adminResult.success) {
            console.log('✅ Admin notification sent for contact form submission');
          } else {
            console.error('❌ Admin notification failed:', adminResult.error);
          }
        } else {
          console.warn('⚠️ Admin notification returned non-JSON response (function may not be deployed)');
        }
      } else {
        console.log('ℹ️ Admin notification skipped in local development mode');
      }
    } catch (error) {
      console.error('❌ Admin notification failed:', error);
    }
    
    // Send confirmation email to user
    let emailSent = false;
    let emailError = null;
    try {
      // Check if we're running locally (Netlify functions won't work)
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        console.warn('⚠️ Running on localhost - Netlify functions not available. Email will not be sent.');
        emailError = 'Email service not available in local development mode. Please deploy to Netlify or use the backend server.';
      } else {
        const emailResponse = await fetch('/.netlify/functions/user-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationType: 'contact_confirmation',
          userData: {
            name: name,
            email: email,
            subject: subject || 'General Inquiry',
            message: message
          }
        })
      });
        
        console.log('📧 Email response status:', emailResponse.status);
        console.log('📧 Email response headers:', Object.fromEntries(emailResponse.headers.entries()));
        
        // Check if response is JSON
        const contentType = emailResponse.headers.get('content-type');
        let emailResult;
        
        if (contentType && contentType.includes('application/json')) {
          emailResult = await emailResponse.json();
          console.log('📧 Email response data:', emailResult);
          
          if (emailResponse.ok && emailResult.success) {
            emailSent = true;
            console.log('✅ Contact confirmation email sent to user:', email);
          } else {
            emailError = emailResult.error || 'Failed to send confirmation email';
            console.error('❌ Email failed:', emailError);
            console.error('❌ Full error response:', emailResult);
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const responseText = await emailResponse.text();
          console.error('❌ Non-JSON response received:', responseText.substring(0, 200));
          
          if (emailResponse.status === 404) {
            emailError = 'Email service not found. Please ensure Netlify functions are deployed.';
          } else if (emailResponse.status >= 500) {
            emailError = 'Email service error. Please try again later.';
          } else {
            emailError = `Email service returned an error (Status: ${emailResponse.status}). Please check Netlify function deployment.`;
          }
        }
      }
    } catch (error) {
      emailError = error.message || 'Failed to send confirmation email';
      console.error('❌ User confirmation email failed with exception:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    }
    
    if (emailSent) {
    setOk("Thanks! We received your message. We'll get back within 1 business day. Check your email for confirmation!");
    } else {
      // Show a more helpful error message
      const errorMsg = emailError 
        ? `(Note: Confirmation email could not be sent: ${emailError}. Your message was still received. Please check your spam folder or contact us directly at omatechdigital@gmail.com)` 
        : '(Note: Confirmation email could not be sent. Your message was still received. Please check your spam folder.)';
      setOk(`Thanks! We received your message. We'll get back within 1 business day. ${errorMsg}`);
    }
    clearForm();
  };
  
  const Field=({label,children,icon})=> (
    <div className="relative">
      <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
        {icon && <span className="text-[#7350FF]">{icon}</span>}
        {label}
      </label>
      {children}
    </div>
  );
  
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16 overflow-hidden">
      {/* Elegant Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="hidden sm:block absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="hidden sm:block absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-slate-100/40 to-blue-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
            Get in Touch
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Let's Build Something
            <span className="block text-slate-700 font-light mt-2">
              Amazing Together
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            Tell us about your project. We usually reply within 24 hours and love turning ideas into reality.
          </p>
        </div>

        {/* Success/Error Messages */}
        {ok && (
          <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700 flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <div className="font-semibold">Message Sent Successfully!</div>
              <div className="text-sm">{ok}</div>
            </div>
          </div>
        )}
        {err && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <div className="font-semibold">Please fix the following:</div>
              <div className="text-sm">{err}</div>
            </div>
          </div>
        )}

        {/* Contact Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Form Header */}
          <div className="bg-slate-900 text-white p-6 md:p-8 text-center">
            <div className="text-3xl md:text-4xl mb-4">📧</div>
            <h2 className="text-xl md:text-2xl font-bold mb-2">Send Us a Message</h2>
            <p className="opacity-90 text-sm md:text-base">We'd love to hear from you and discuss your project</p>
          </div>

          {/* Form Content */}
          <form ref={formRef} className="p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Field label="Full Name" icon="👤">
                <input 
                  name="name"
                  className="w-full px-3 md:px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400 text-sm md:text-base" 
                  placeholder="Enter your full name"
            />
          </Field>
              <Field label="Email Address" icon="📧">
                <input 
                  name="email"
                  type="email"
                  className="w-full px-3 md:px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400 text-sm md:text-base" 
              placeholder="you@example.com"
            />
          </Field>
              <Field label="Phone Number" icon="📱">
                <input 
                  name="phone"
                  className="w-full px-3 md:px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400 text-sm md:text-base" 
                  placeholder="+91 98765 43210"
            />
          </Field>
              <Field label="Subject" icon="📝">
                <input 
                  name="subject"
                  className="w-full px-3 md:px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400 text-sm md:text-base" 
                  placeholder="What's this about?"
            />
          </Field>
        </div>
            
            <Field label="Your Message" icon="💭">
              <textarea 
                name="message"
                rows={5}
                className="w-full px-3 md:px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400 resize-none text-sm md:text-base" 
                placeholder="Tell us about your project, goals, timeline, or any questions you have..."
          />
        </Field>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4">
              <button 
                type="button"
                onClick={submit} 
                className="flex-1 px-6 md:px-8 py-3 md:py-4 rounded-lg text-white font-semibold text-base md:text-lg shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6]"
              >
                <span className="flex items-center justify-center gap-2">
                  <span>🚀</span>
                  Send Message
                </span>
              </button>
              <button 
                type="button"
                onClick={clearForm} 
                className="px-6 md:px-8 py-3 md:py-4 rounded-lg border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-2">
                  <span>🔄</span>
                  Clear Form
                </span>
              </button>
            </div>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="text-center p-4 md:p-6 rounded-xl bg-white shadow-sm border border-slate-200 group hover:shadow-md transition-all duration-200">
            <div className="text-2xl md:text-3xl mb-3">📧</div>
            <h3 className="font-semibold text-slate-900 mb-2 text-sm md:text-base">Email Us</h3>
            <p className="text-slate-600">hello@omatechdigital.com</p>
          </div>
          <div className="text-center p-4 md:p-6 rounded-xl bg-white shadow-sm border border-slate-200 group hover:shadow-md transition-all duration-200">
            <div className="text-2xl md:text-3xl mb-3">📱</div>
            <h3 className="font-semibold text-slate-900 mb-2">Call Us</h3>
            <p className="text-slate-600">+91 8779157670</p>
          </div>
          <div className="text-center p-4 md:p-6 rounded-xl bg-white shadow-sm border border-slate-200 group hover:shadow-md transition-all duration-200">
            <div className="text-2xl md:text-3xl mb-3">⏰</div>
            <h3 className="font-semibold text-slate-900 mb-2">Response Time</h3>
            <p className="text-slate-600">Within 24 hours</p>
          </div>
        </div>
      </div>
    </main>
  );
}

export function PleaseLogin({goLogin}){
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
        <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mt-2">Please log in to continue</h3>
      <p className="text-slate-600 mt-2">You need an account to access this feature.</p>
      <button onClick={goLogin} className="mt-6 px-6 py-3 rounded-lg text-white bg-slate-900 hover:bg-slate-800 transition-colors duration-200 font-semibold shadow-sm hover:shadow-md">Go to Login</button>
    </div>
  );
}

export function Register({users,setUsers,goLogin}){
  const formRef = useRef(null);
  const [emailOtpPhase,setEmailOtpPhase]=useState(0); // 0 none, 1 sent, 2 verified
  const [showPassword,setShowPassword]=useState(false);
  const [msg,setMsg]=useState("");
  const [err,setErr]=useState("");
  const [emailSending,setEmailSending]=useState(false);
  const [isSubmitting,setIsSubmitting]=useState(false);
  
  // Form field states to preserve values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    otp: '',
    agree: false
  });
  
  const normalize=(p)=>{const s=(p||"").replace(/[\s-]/g,"");const ind=/(?:^\+91|^0)?([6-9]\d{9})$/;if(ind.test(s)){const m=s.match(ind);return "+91"+m[1]}if(/^\+?[1-9]\d{7,14}$/.test(s))return s.startsWith("+")?s:"+"+s;return null};
  
  // Handle input changes with useCallback to prevent re-renders
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  
  // Clear form function
  const clearForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      otp: '',
      agree: false
    });
    setErr("");
    setMsg("");
    setEmailOtpPhase(0);
  };
  
  // Email verification functions
  const sendEmailCode=useCallback(async()=>{
    setErr("");
    setMsg("");
    
    const email = formData.email;
    
    if(!email)return setErr("Please enter your email address first.");
    if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))return setErr("Please enter a valid email address.");
    
    // Check if email exists on server
    try {
      const checkResponse = await fetch('/.netlify/functions/user-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'check_email',
          userData: { email }
        })
      });
      
      const checkData = await checkResponse.json();
      if (checkData.exists) {
        return setErr("Email already registered. Please try logging in instead.");
      }
    } catch (error) {
      console.warn('Email check failed, proceeding with registration:', error);
      // Continue with registration if check fails
    }
    
    try {
      setEmailSending(true);
      setMsg("Sending verification code...");
      // Generate a 6-digit verification code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Check if we're in development mode (localhost)
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        // Local development fallback - show code directly
        setEmailOtpPhase(1);
        setMsg(`🔧 Development Mode: Your verification code is ${code}. (Email sending disabled on localhost)`);
        
        // Store the code for verification (10 minutes expiry)
        localStorage.setItem("om_email_otp_"+email,JSON.stringify({
          code: code,
          exp: Date.now() + 10 * 60 * 1000
        }));
        
        setEmailSending(false);
        return;
      }
      
      // Production: Send real email via Netlify Functions
      const controller = new AbortController();
      const timeoutId = setTimeout(()=>controller.abort(), 15000);
      let response;
      try {
        response = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, code, type: 'verification' }),
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }
      
      let result;
      try {
        result = await response.json();
      } catch(parseErr) {
        result = { success: false, error: `Invalid response (${response.status})` };
      }
      
      if (response.ok && result.success) {
        setEmailOtpPhase(1);
        setMsg(`📧 Verification code sent to ${email}. Check your inbox!`);
        
        // Store the code for verification (10 minutes expiry)
        localStorage.setItem("om_email_otp_"+email,JSON.stringify({
          code: code,
          exp: Date.now() + 10 * 60 * 1000
        }));
      } else {
        const errMsg = result?.error || `Request failed (${response.status})`;
        setErr("Failed to send verification code: " + errMsg);
      }
      
    } catch (error) {
      console.error("Email verification error:", error);
      setErr("Failed to send verification code. Please try again.");
    } finally {
      setEmailSending(false);
    }
  }, [formData.email, users]);
  
  const verifyEmailCode=useCallback(()=>{
    setErr("");
    
    const emailOtp = formData.otp;
    const email = formData.email;
    
    if(!emailOtp)return setErr("Please enter the verification code.");
    
    const rec=JSON.parse(localStorage.getItem("om_email_otp_"+email)||"null");
    if(!rec)return setErr("Please request a verification code first.");
    if(Date.now()>rec.exp)return setErr("Verification code expired. Please request a new one.");
    if(emailOtp.trim()!==rec.code)return setErr("Incorrect verification code.");
    
    setEmailOtpPhase(2);
    setMsg("Email verified ✅");
    setErr(""); // Clear any previous errors
  }, [formData.otp, formData.email]);

  const resendEmailCode = useCallback(() => {
    setEmailOtpPhase(0);
    setMsg("");
    setErr("");
    // Clear only the OTP field, keep other form data
    setFormData(prev => ({
      ...prev,
      otp: ''
    }));
    sendEmailCode();
  }, [sendEmailCode]);
  
  const Field = useCallback(({label, children}) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {children}
    </div>
  ), []);

  const submit=useCallback(async()=>{
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    setErr("");
    setMsg("");
    
    const { name, email, password, phone, agree } = formData;
    
    if(!name||!email||!password)return setErr("All fields are required.");
    if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))return setErr("Invalid email.");
    const np=normalize(phone);
    if(!np)return setErr("Enter a valid mobile number.");
    if(emailOtpPhase!==2)return setErr("Please verify email with verification code.");
    if(!agree)return setErr("Please accept Terms & Privacy.");
    
    setIsSubmitting(true);
    setMsg("Creating your account...");
    
    try {
      // Check if this is the admin email
      const isAdmin = email.toLowerCase() === 'omatechdigital@gmail.com';
      
      // Register user on server
      console.log('🚀 Registering user on server:', email);
      const response = await fetch('/.netlify/functions/user-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'register',
          userData: {
            name,
            email,
            password,
            phone: np,
            role: isAdmin ? "admin" : "user"
          }
        })
      });
      
      const responseData = await response.json();
      console.log('📧 Registration response:', responseData);
      
      if (!response.ok) {
        if (response.status === 409) {
          setErr("This email is already registered. Please try logging in instead.");
        } else {
          setErr(responseData.error || "Registration failed. Please try again.");
        }
        return;
      }
      
      // Update local state with new user
      setUsers(p=>[...p,{
        id: responseData.user.id,
        name: responseData.user.name,
        email: responseData.user.email,
        password,
        role: responseData.user.role,
        phone: responseData.user.phone,
        phoneVerified: responseData.user.phoneVerified,
        emailVerified: responseData.user.emailVerified,
        isAdmin: isAdmin
      }]);
      
      const roleMessage = isAdmin ? " with ADMIN privileges" : "";
      console.log(`✅ Registration successful${roleMessage}!`);
      
      // Track user registration
      analytics.trackRegistration(email, isAdmin ? "admin" : "user");
      
      // Send admin notification for new user registration
      try {
        await fetch('/.netlify/functions/admin-notification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            notificationType: 'user_registration',
            userData: {
              email: email,
              phone: np,
              name: name,
              role: isAdmin ? "admin" : "user",
              registrationTime: new Date().toLocaleString()
            }
          })
        });
        console.log('📧 Admin notification sent for new user registration');
      } catch (error) {
        console.warn('Admin notification failed:', error);
        // Don't fail the registration if admin notification fails
      }
      
      setMsg("Account created successfully! Redirecting to login...");
      
      // Wait a moment before redirecting to show success message
      setTimeout(() => {
        goLogin();
      }, 2000);
      
    } catch (error) {
      console.error("Registration error:", error);
      setErr("Registration failed. Please try again.");
      setMsg("");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, formData, emailOtpPhase, users, setUsers, goLogin]);
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16 overflow-hidden">
      {/* Elegant Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="hidden sm:block absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="hidden sm:block absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-slate-100/40 to-blue-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4">
        {/* Header with back button */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-slate-900">Create Account</h2>
          <p className="text-slate-600 text-base">Join Omatech Digital to get started</p>
        </div>
        
        {/* Back to Login button */}
        <div className="text-center mb-6">
          <button 
            onClick={goLogin} 
            className="inline-flex items-center gap-2 text-[#7350FF] hover:text-[#8B00FF] text-sm font-medium transition-colors duration-200"
          >
            ← Back to Login
          </button>
        </div>
        
        <form ref={formRef} className="rounded-2xl bg-white border border-slate-200 shadow-xl p-6 md:p-8 space-y-6">
        {err&&<div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
        {msg&&<div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">{msg}</div>}
        <Field label="Full name">
          <input 
            name="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
            placeholder="Enter your full name"
          />
        </Field>
        <Field label="Email">
          <div className="flex gap-3">
            <input 
              name="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
              placeholder="you@example.com"
            />
            {emailOtpPhase!==2 ? (
              <button 
                type="button"
                onClick={sendEmailCode} 
                className="px-4 py-3 rounded-lg text-white transition-all duration-200 hover:bg-slate-800 bg-slate-900 shadow-sm hover:shadow-md"
                disabled={emailSending}
              >
                {emailSending ? 'Sending…' : 'Send Code'}
              </button>
            ) : (
              <span className="px-4 py-3 text-sm rounded-lg bg-green-50 border border-green-200 text-green-700">Verified ✅</span>
            )}
          </div>
          {emailOtpPhase===1 && (
            <div className="mt-3 space-y-3">
              <div className="flex gap-3">
                <input 
                  name="otp"
                  value={formData.otp}
                  onChange={(e) => handleInputChange('otp', e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  pattern="[0-9]*"
                />
                <button 
                  type="button"
                  onClick={verifyEmailCode} 
                  className="px-4 py-3 rounded-lg text-white transition-all duration-200 hover:bg-slate-800 bg-slate-900 shadow-sm hover:shadow-md"
                >
                  Verify
                </button>
              </div>
              <button 
                type="button"
                onClick={resendEmailCode}
                className="text-sm text-[#7350FF] hover:text-[#8B00FF] underline"
                disabled={emailSending}
              >
                {emailSending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-2">
            📧 Click "Send Code" to receive verification code
            {window.location.hostname === 'localhost' && (
              <span className="block text-[#7350FF] mt-1">
                🔧 Development Mode: Code will be displayed directly (no email sent)
              </span>
            )}
          </p>
        </Field>
        
        <Field label="Password">
          <div className="relative">
            <input 
              name="password"
              type={showPassword ? "text" : "password"} 
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full px-4 py-3 pr-12 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
        </Field>
        <Field label="Mobile number">
              <input 
                name="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
                placeholder="e.g., +91 98765 43210"
              />
          <p className="text-xs text-slate-500 mt-2">📱 Enter your mobile number (no verification required)</p>
        </Field>
          

        <label className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
          <input 
            name="agree"
            type="checkbox" 
            checked={formData.agree}
            onChange={(e) => handleInputChange('agree', e.target.checked)}
            className="w-4 h-4 rounded border border-slate-300 bg-white text-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20"
          /> 
          I agree to Terms & Privacy Policy
        </label>
        <button 
          type="button"
          onClick={submit} 
          disabled={emailOtpPhase!==2 || isSubmitting} 
          className={`w-full py-3 rounded-lg text-white font-semibold text-base transition-all duration-200 ${
            emailOtpPhase!==2 || isSubmitting
              ? 'bg-slate-400 cursor-not-allowed' 
              : 'bg-slate-900 hover:bg-slate-800 hover:shadow-lg'
          }`}
        >
          {isSubmitting ? 'Creating Account...' : emailOtpPhase!==2 ? 'Verify Email First' : 'Create Account'}
        </button>
        <div className="text-sm text-center text-slate-600">Already have an account? <span className="text-[#7350FF] cursor-pointer hover:text-[#8B00FF] hover:underline transition-colors duration-200" onClick={goLogin}>Login</span></div>
        </form>
      </div>
    </main>
  );
}

export function Login({users,onLogged,goForgotPassword,goSignUp}){
  const formRef = useRef(null);
  const [err,setErr]=useState("");
  const [isSubmitting,setIsSubmitting]=useState(false);
  
  const submit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    if (!formRef.current) return;
    
    const formData = new FormData(formRef.current);
    const email = formData.get('email');
    const password = formData.get('password');
    
    setIsSubmitting(true);
    setErr("");
    
    try {
      // Validate inputs
      if (!email.trim()) {
        setErr("Please enter your email address.");
        setIsSubmitting(false);
        return;
      }
      if (!password.trim()) {
        setErr("Please enter your password.");
        setIsSubmitting(false);
        return;
      }
      
      // Authenticate with server
      console.log('🚀 Authenticating user on server:', email);
      const response = await fetch('/.netlify/functions/user-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          userData: {
            email,
            password
          }
        })
      });
      
      const responseData = await response.json();
      console.log('📧 Login response:', responseData);
      
      if (!response.ok) {
        setErr(responseData.error || "Invalid email or password. Please try again.");
        setIsSubmitting(false);
        return;
      }
      
      // Update local users array with server data
      const serverUser = responseData.user;
      setUsers(prevUsers => {
        const existingUserIndex = prevUsers.findIndex(u => u.email === serverUser.email);
        const updatedUser = {
          id: serverUser.id,
          name: serverUser.name,
          email: serverUser.email,
          password, // Keep the plain password for local use
          role: serverUser.role,
          phone: serverUser.phone,
          phoneVerified: serverUser.phoneVerified,
          emailVerified: serverUser.emailVerified,
          isAdmin: serverUser.role === 'admin'
        };
        
        if (existingUserIndex >= 0) {
          // Update existing user
          const newUsers = [...prevUsers];
          newUsers[existingUserIndex] = updatedUser;
          return newUsers;
        } else {
          // Add new user
          return [...prevUsers, updatedUser];
        }
      });
      
      // Clear any previous errors
      setErr("");
      
      // Call the login callback
      onLogged(email);
      
    } catch (error) {
      console.error('Login error:', error);
      setErr("An error occurred during login. Please try again.");
      setIsSubmitting(false);
    }
  };
  
  // Clear form function
  const clearForm = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setErr("");
  };

  const Field = ({label, children}) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {children}
    </div>
  );
  
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16 overflow-hidden">
      {/* Elegant Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="hidden sm:block absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="hidden sm:block absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-slate-100/40 to-blue-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-slate-900">Welcome Back</h2>
          <p className="text-slate-600 text-base">Sign in to your account</p>
        </div>
        
        <form ref={formRef} className="rounded-2xl bg-white border border-slate-200 shadow-xl p-6 md:p-8 space-y-6">
        {err && (
          <div key="err" className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {err}
          </div>
        )}
        <Field label="Email">
          <input 
            name="email"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
            placeholder="you@example.com"
            disabled={isSubmitting}
          />
        </Field>
        <Field label="Password">
          <input 
            name="password"
            type="password" 
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#7350FF] focus:ring-2 focus:ring-[#7350FF]/20 transition-all duration-200 bg-white text-slate-900 placeholder-slate-400" 
            placeholder="Enter your password"
            disabled={isSubmitting}
          />
        </Field>
        <button 
          type="button"
          onClick={submit} 
          disabled={isSubmitting}
          className={`w-full py-3 rounded-lg text-white font-semibold text-base transition-all duration-200 ${
            isSubmitting 
              ? 'bg-slate-400 cursor-not-allowed' 
              : 'bg-slate-900 hover:bg-slate-800 hover:shadow-lg'
          }`}
        >
          {isSubmitting ? 'Signing in...' : 'Sign In'}
        </button>
        
        {/* Action buttons section */}
        <div className="text-center space-y-4 pt-4">
          <button type="button" onClick={goForgotPassword} className="text-[#7350FF] hover:text-[#8B00FF] hover:underline text-sm transition-colors duration-200">
            Forgot your password?
          </button>
          
          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">Don't have an account?</span>
            </div>
          </div>
          
          {/* Sign up button */}
          <button 
            type="button"
            onClick={goSignUp} 
            className="w-full py-3 px-4 rounded-lg border-2 border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 font-medium"
          >
            Create Account
          </button>
        </div>
        </form>
      </div>
    </main>
  );
}

export function Forgot({users,setUsers,goLogin}){
  const [email,setEmail]=useState("");
  const [resetCode,setResetCode]=useState("");
  const [newPassword,setNewPassword]=useState("");
  const [confirmPassword,setConfirmPassword]=useState("");
  const [phase,setPhase]=useState(1);
  const [msg,setMsg]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  
  const sendResetEmail=async()=>{
    setErr("");
    setLoading(true);
    
    if(!email) {
      setErr("Please enter your email address.");
      setLoading(false);
      return;
    }
    
    if(!users.some(u=>u.email===email)) {
      setErr("Email not found. Please check your email or create a new account.");
      setLoading(false);
      return;
    }
    
    try {
      // Generate 6-digit reset code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Store reset code with expiry (15 minutes)
      localStorage.setItem("om_reset_"+email,JSON.stringify({
        code,
        exp:Date.now()+15*60*1000
      }));
      
      // Send reset email using our email service
      console.log('🔐 Attempting to send password reset email to:', email);
      
      try {
        const { sendPasswordResetEmail } = await import('./realEmailService.js');
        console.log('✅ Email service imported successfully');
        
        const emailResult = await sendPasswordResetEmail(email, code);
        console.log('📧 Email result:', emailResult);
        
        if(emailResult.success) {
          setMsg("📧 Password reset code sent to your email! Check your inbox and enter the code below to reset your password.");
          // Don't auto-set the reset code - let user enter it manually
          setPhase(2);
        } else {
          setErr(`Failed to send reset email: ${emailResult.error || 'Unknown error'}`);
        }
      } catch (importError) {
        console.error('❌ Failed to import email service:', importError);
        setErr("Failed to load email service. Please try again.");
      }
    } catch (error) {
      console.error("Reset email error:", error);
      setErr("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const verifyAndReset=()=>{
    setErr("");
    
    if(!resetCode)return setErr("Please enter the reset code from your email.");
    if(!newPassword)return setErr("Please enter a new password.");
    if(newPassword.length < 6)return setErr("Password must be at least 6 characters long.");
    if(newPassword !== confirmPassword)return setErr("Passwords do not match.");
    
    // Get the stored reset code for this email
    const rec=JSON.parse(localStorage.getItem("om_reset_"+email)||"null");
    if(!rec)return setErr("Please request a reset code first.");
    if(rec.code!==resetCode)return setErr("Invalid reset code. Please check your email.");
    if(Date.now()>rec.exp)return setErr("Reset code has expired. Please request a new one.");
    
    // Update password
    setUsers(users.map(u=>u.email===email?{...u,password:newPassword}:u));
    
    // Clear reset data
    localStorage.removeItem("om_reset_"+email);
    
    setMsg("✅ Password updated successfully! You can now login with your new password.");
    setPhase(3);
  };
  
  const goBackToLogin=()=>{
    goLogin();
  };
  
  // Clear error when user starts typing
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (err) setErr("");
  };
  
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16 overflow-hidden">
      {/* Elegant Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="hidden sm:block absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="hidden sm:block absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-slate-100/40 to-blue-50/30 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute top-1/4 left-0 w-48 h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent"></div>
      <div className="absolute bottom-1/4 right-0 w-48 h-px bg-gradient-to-l from-transparent via-cyan-400/30 to-transparent"></div>
      
      <div className="max-w-md mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
            Password Reset
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">
            Reset Password
          </h2>
          <p className="text-slate-600 text-lg">Secure your account with a new password</p>
        </div>

        {/* Main Form Container */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-6 md:p-8 space-y-6">
          {/* Messages */}
          {msg && <div key="message" className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-3">
            <span className="text-xl">✅</span>
            {msg}
          </div>}
          {err && <div key="error" className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-3">
            <span className="text-xl">⚠️</span>
            {err}
          </div>}
          
          {/* Phase 1: Email Entry */}
          {phase === 1 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-slate-100 text-2xl flex items-center justify-center">
                  📧
                </div>
                <p className="text-slate-600 text-sm">Enter your email address and we'll send you a password reset verification code.</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Email Address</label>
                  <input 
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#7350FF]/20 focus:border-[#7350FF] transition-all duration-200" 
                    value={email} 
                    onChange={handleEmailChange} 
                    placeholder="you@example.com"
                    disabled={loading}
                  />
                </div>
                
                <button 
                  onClick={sendResetEmail} 
                  disabled={loading}
                  className="w-full px-6 py-3 rounded-lg text-white font-semibold transition-all duration-200 hover:bg-slate-800 bg-slate-900 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed" 
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin"></div>
                      Sending...
                    </span>
                  ) : (
                    "Send Reset Code"
                  )}
                </button>
                
                <div className="text-center pt-4">
                  <button onClick={goBackToLogin} className="text-[#7350FF] hover:text-[#8B00FF] transition-colors duration-200 text-sm flex items-center gap-2 mx-auto">
                    <span>←</span>
                    Back to Login
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Phase 2: Password Reset */}
          {phase === 2 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-slate-100 text-2xl flex items-center justify-center">
                  🔐
                </div>
                <p className="text-slate-600 text-sm">Enter the reset code from your email and create a new password for your account.</p>
              </div>
              
              {/* Reset Code input - visible to user */}
              <div>
                <label className="block text-slate-700 text-sm font-medium mb-2">Reset Code</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200" 
                  value={resetCode} 
                  onChange={e => setResetCode(e.target.value)} 
                  placeholder="Enter 6-digit code from email"
                  maxLength="6"
                  pattern="[0-9]*"
                />
                <p className="text-slate-500 text-xs mt-1">Enter the 6-digit code sent to your email</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">New Password</label>
                  <input 
                    type="password" 
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#7350FF]/20 focus:border-[#7350FF] transition-all duration-200" 
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    placeholder="••••••••"
                  />
                </div>
                
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Confirm New Password</label>
                  <input 
                    type="password" 
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#7350FF]/20 focus:border-[#7350FF] transition-all duration-200" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="••••••••"
                  />
                </div>
                
                <button 
                  onClick={verifyAndReset} 
                  className="w-full px-6 py-3 rounded-lg text-white font-semibold transition-all duration-200 hover:bg-slate-800 bg-slate-900 shadow-sm hover:shadow-md" 
                >
                  Reset Password
                </button>
                
                <div className="text-center space-y-3 pt-4">
                  <button onClick={()=>setPhase(1)} className="text-[#7350FF] hover:text-[#8B00FF] transition-colors duration-200 text-sm flex items-center gap-2 mx-auto">
                    <span>←</span>
                    Back to Email Entry
                  </button>
                  <div>
                    <button onClick={goBackToLogin} className="text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm">
                      Back to Login
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Phase 3: Success */}
          {phase === 3 && (
            <>
              <div className="text-center space-y-6">
                <div className="w-20 h-20 mx-auto rounded-xl bg-green-50 text-4xl flex items-center justify-center">
                  ✅
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Password Reset Successful!</h3>
                  <p className="text-slate-600">Your password has been updated. You can now login with your new password.</p>
                </div>
                <button 
                  onClick={goBackToLogin} 
                  className="w-full px-8 py-4 rounded-lg text-white font-semibold transition-all duration-200 hover:bg-slate-800 bg-slate-900 shadow-sm hover:shadow-md" 
                >
                  Go to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export function Dashboard({me,myReqs,newReq,goMy,allUsers,allReqs,setAllUsers,setAllReqs,adminPanelOpen}){
  const total=myReqs.length, sub=myReqs.filter(r=>r.status==="Submitted").length, done=myReqs.filter(r=>r.status==="Completed").length;
  const isAdmin = me?.isAdmin || me?.role === 'admin';
  const [adminPanelVisible, setAdminPanelVisible] = useState(adminPanelOpen || false);
  const [keywordAnalysisVisible, setKeywordAnalysisVisible] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [keywordResults, setKeywordResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Auto-open admin panel if adminPanelOpen prop is true
  useEffect(() => {
    if (adminPanelOpen && isAdmin) {
      setAdminPanelVisible(true);
    }
  }, [adminPanelOpen, isAdmin]);
  
  const toggleAdminPanel = () => {
    setAdminPanelVisible(!adminPanelVisible);
  };

  const toggleKeywordAnalysis = () => {
    setKeywordAnalysisVisible(!keywordAnalysisVisible);
  };

  const analyzeWebsite = async () => {
    if (!websiteUrl.trim()) {
      alert('Please enter a website URL');
      return;
    }

    setIsAnalyzing(true);
    setKeywordResults(null);

    try {
      // Simulate professional keyword analysis
      await new Promise(resolve => setTimeout(resolve, 2000));

      const mockResults = {
        website: websiteUrl,
        analyzedAt: new Date().toLocaleString(),
        summary: {
          totalKeywords: 47,
          highValueKeywords: 12,
          mediumValueKeywords: 23,
          lowValueKeywords: 12,
          averageSearchVolume: 2400,
          averageCompetition: 'Medium'
        },
        topKeywords: [
          { keyword: 'web development services', searchVolume: 5400, competition: 'High', cpc: 12.50, difficulty: 78 },
          { keyword: 'digital marketing agency', searchVolume: 3200, competition: 'Medium', cpc: 8.75, difficulty: 65 },
          { keyword: 'SEO optimization', searchVolume: 2800, competition: 'Medium', cpc: 7.20, difficulty: 62 },
          { keyword: 'website design company', searchVolume: 2100, competition: 'Low', cpc: 5.80, difficulty: 45 },
          { keyword: 'ecommerce development', searchVolume: 1800, competition: 'Medium', cpc: 9.40, difficulty: 58 },
          { keyword: 'mobile app development', searchVolume: 1600, competition: 'High', cpc: 11.20, difficulty: 72 },
          { keyword: 'content marketing strategy', searchVolume: 1400, competition: 'Low', cpc: 4.60, difficulty: 38 },
          { keyword: 'social media management', searchVolume: 1200, competition: 'Medium', cpc: 6.80, difficulty: 52 },
          { keyword: 'PPC advertising', searchVolume: 1100, competition: 'High', cpc: 10.50, difficulty: 68 },
          { keyword: 'brand identity design', searchVolume: 900, competition: 'Low', cpc: 3.90, difficulty: 42 }
        ],
        longTailKeywords: [
          { keyword: 'best web development services in india', searchVolume: 320, competition: 'Low', cpc: 4.20, difficulty: 28 },
          { keyword: 'affordable digital marketing agency near me', searchVolume: 280, competition: 'Low', cpc: 3.80, difficulty: 25 },
          { keyword: 'professional SEO services for small business', searchVolume: 240, competition: 'Medium', cpc: 5.60, difficulty: 35 },
          { keyword: 'custom website design for restaurants', searchVolume: 180, competition: 'Low', cpc: 2.90, difficulty: 22 },
          { keyword: 'ecommerce website development cost india', searchVolume: 160, competition: 'Medium', cpc: 4.80, difficulty: 32 }
        ],
        recommendations: [
          'Focus on long-tail keywords with lower competition for better ranking opportunities',
          'Target local SEO keywords to capture regional market share',
          'Create content around "web development services" and "digital marketing agency"',
          'Consider PPC campaigns for high-value keywords with medium competition',
          'Develop landing pages for each major service category'
        ]
      };

      setKeywordResults(mockResults);
    } catch (error) {
      alert('Error analyzing website. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  return (
    <main className="max-w-7xl mx-auto px-4 py-12 bg-white min-h-screen">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-slate-900">
          {adminPanelVisible ? '🔧 Admin Dashboard' : `Welcome, ${me?.name||me?.email}`}
        </h2>
        <p className="text-slate-600 mt-2">
          {adminPanelVisible ? 'Manage users, requirements, and system operations' : 'Track your requests and start new ones.'}
        </p>
        {isAdmin && (
          <div className="mt-4 p-3 bg-slate-900 text-white rounded-lg">
            <span className="font-semibold">👑 ADMIN PANEL</span> - You have full administrative privileges
          </div>
        )}
      </div>
      
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Stat label="Total Requests" value={total}/>
        <Stat label="Submitted" value={sub}/>
        <Stat label="Completed" value={done}/>
      </div>
      
      <div className="flex gap-3 mb-6 flex-wrap">
        <button onClick={newReq} className="px-5 py-2 rounded-lg text-white bg-slate-900 hover:bg-slate-800 transition-colors duration-200 shadow-sm hover:shadow-md">＋ New Requirement</button>
        <button onClick={goMy} className="px-5 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors duration-200">View My Requests</button>
        {isAdmin && (
          <button onClick={toggleAdminPanel} className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors duration-200">
            {adminPanelVisible ? '🔧 Hide Admin Panel' : '🔧 Admin Panel'}
          </button>
        )}
      </div>
      
      {/* Admin Panel */}
      {isAdmin && adminPanelVisible && (
        <div id="admin-panel" className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
          <h3 className="text-2xl font-bold mb-4 text-slate-900">🔧 Admin Panel</h3>
          
          {/* Admin Tools */}
          <div className="mb-6 p-4 bg-white rounded-lg border border-slate-200">
            <h4 className="text-lg font-semibold mb-3 text-slate-900">🛠️ Admin Tools</h4>
            <div className="flex gap-3 flex-wrap">
              <button 
                onClick={() => {
                  const adminUser = {
                    email: "omatechdigital@gmail.com",
                    name: "Omatech Digital Admin",
                    password: "admin123",
                    role: "admin",
                    phone: "+918779157670",
                    phoneVerified: true,
                    emailVerified: true,
                    isAdmin: true
                  };
                  setAllUsers(prev => [adminUser, ...prev]);
                  alert("Admin account created! You can now login with:\nEmail: omatechdigital@gmail.com\nPassword: admin123");
                }}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors duration-200 text-sm"
              >
                👑 Create Admin Account
              </button>
              <button 
                onClick={() => {
                  const currentUsers = allUsers || [];
                  alert(`Current users in system: ${currentUsers.length}\n\nUsers:\n${currentUsers.map(u => `${u.email} (${u.role})`).join('\n')}`);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm"
              >
                👥 Check Users
              </button>
              <button 
                onClick={toggleKeywordAnalysis}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm"
              >
                🔍 Keyword Analysis
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2">
              💡 <strong>First time setup:</strong> If you're the first admin, create an admin account first, then logout and login with the new credentials.
            </p>
          </div>

          {/* Keyword Analysis Tool */}
          {keywordAnalysisVisible && (
            <div className="mb-6 p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="text-lg font-semibold mb-3 text-slate-900">🔍 Professional Keyword Analysis</h4>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Website URL to Analyze</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#7350FF]/20 focus:border-[#7350FF] transition-all duration-200"
                  />
                  <button
                    onClick={analyzeWebsite}
                    disabled={isAnalyzing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing ? '🔍 Analyzing...' : '🔍 Analyze'}
                  </button>
                </div>
              </div>

              {keywordResults && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border">
                    <h5 className="font-semibold text-gray-800 mb-3">📊 Analysis Summary</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#7350FF]">{keywordResults.summary.totalKeywords}</div>
                        <div className="text-gray-600">Total Keywords</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{keywordResults.summary.highValueKeywords}</div>
                        <div className="text-gray-600">High Value</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">{keywordResults.summary.averageSearchVolume.toLocaleString()}</div>
                        <div className="text-gray-600">Avg Search Volume</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{keywordResults.summary.averageCompetition}</div>
                        <div className="text-gray-600">Avg Competition</div>
                      </div>
                    </div>
                  </div>

                  {/* Top Keywords */}
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">🔥 Top Keywords</h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left">Keyword</th>
                            <th className="px-3 py-2 text-left">Search Volume</th>
                            <th className="px-3 py-2 text-left">Competition</th>
                            <th className="px-3 py-2 text-left">CPC</th>
                            <th className="px-3 py-2 text-left">Difficulty</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {keywordResults.topKeywords.map((kw, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium">{kw.keyword}</td>
                              <td className="px-3 py-2">{kw.searchVolume.toLocaleString()}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  kw.competition === 'High' ? 'bg-red-100 text-red-800' :
                                  kw.competition === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {kw.competition}
                                </span>
                              </td>
                              <td className="px-3 py-2">${kw.cpc}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        kw.difficulty >= 70 ? 'bg-red-500' :
                                        kw.difficulty >= 50 ? 'bg-yellow-500' :
                                        'bg-green-500'
                                      }`}
                                      style={{width: `${kw.difficulty}%`}}
                                    ></div>
                                  </div>
                                  <span className="text-xs">{kw.difficulty}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Long Tail Keywords */}
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">🎯 Long Tail Keywords</h5>
                    <div className="grid gap-3">
                      {keywordResults.longTailKeywords.map((kw, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-sm">{kw.keyword}</span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              kw.competition === 'High' ? 'bg-red-100 text-red-800' :
                              kw.competition === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {kw.competition}
                            </span>
                          </div>
                          <div className="flex gap-4 text-xs text-gray-600">
                            <span>Volume: {kw.searchVolume}</span>
                            <span>CPC: ${kw.cpc}</span>
                            <span>Difficulty: {kw.difficulty}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">💡 Strategic Recommendations</h5>
                    <div className="space-y-2">
                      {keywordResults.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                          <span className="text-[#7350FF] mt-0.5">•</span>
                          <span className="text-sm text-gray-700">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 text-center">
                    Analysis completed at: {keywordResults.analyzedAt}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* User Management */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-3 text-gray-700">👥 User Management</h4>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allUsers?.map((user, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{user.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.phone}</td>
                        <td className="px-4 py-3 text-sm">
                          {user.email !== 'omatechdigital@gmail.com' && (
                            <button 
                              onClick={() => {
                                if(confirm(`Are you sure you want to delete user ${user.name}?`)) {
                                  setAllUsers(prev => prev.filter((_, i) => i !== index));
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* Requirement Management */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-700">📋 Requirement Management</h4>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allReqs?.map((req, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{req.userName || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{req.title || 'No title'}</td>
                        <td className="px-4 py-3 text-sm">
                          <select 
                            value={req.status || 'Submitted'} 
                            onChange={(e) => {
                              const newReqs = [...allReqs];
                              newReqs[index].status = e.target.value;
                              setAllReqs(newReqs);
                            }}
                            className="px-2 py-1 text-xs border rounded bg-white"
                          >
                            <option value="Submitted">Submitted</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {req.date ? new Date(req.date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button 
                            onClick={() => {
                              if(confirm(`Are you sure you want to delete this requirement?`)) {
                                setAllReqs(prev => prev.filter((_, i) => i !== index));
                              }
                            }}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <KeywordsMini/>
    </main>
  );
}

export function KeywordsMini(){
  const [biz,setBiz]=useState("");
  const [loc,setLoc]=useState("");
  const [out,setOut]=useState([]);
  const gen=()=>{const b=biz.trim()||"service";const L=loc.trim();setOut([`${b} near me`,`${b} services`,`${b} pricing`,`${b} best`,`${b} top`,`${b} ${L}`,`${b} agency ${L}`,`${b} online`,`${b} expert`,`${b} reviews`])};
  return (
    <div className="rounded-2xl bg-white border shadow p-6">
      <div className="font-semibold mb-2">Get Best Keywords for My Business</div>
      <div className="grid md:grid-cols-3 gap-3">
        <input 
          className="border rounded-lg px-3 py-2" 
          placeholder="Business (e.g., dental clinic)" 
          value={biz} 
          onChange={e => setBiz(e.target.value)}
        />
        <input 
          className="border rounded-lg px-3 py-2" 
          placeholder="Location (e.g., Mumbai)" 
          value={loc} 
          onChange={e => setLoc(e.target.value)}
        />
        <button onClick={gen} className="px-4 py-2 rounded-lg text-white" style={{backgroundImage:"linear-gradient(to right,#4f46e5,#06b6d4)"}}>Generate</button>
      </div>
      {out.length>0 && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-gray-700">{out.map((k,i)=>(<div key={i} className="p-2 rounded-lg bg-gray-50 border">{k}</div>))}</div>
          <button onClick={()=>navigator.clipboard.writeText(out.join("\n"))} className="mt-3 px-4 py-2 rounded-lg border">Copy</button>
        </>
      )}
    </div>
  );
}

export function NewReq({onSubmit,onCancel}){
  const formRef = useRef(null);
  const [err, setErr] = useState("");
  const [objectives, setObjectives] = useState([]);
  
  const toggle = (v) => {
    setObjectives(prev => 
      prev.includes(v) 
        ? prev.filter(x => x !== v) 
        : [...prev, v]
    );
  };
  
    const submit = () => {
    setErr("");
    
    if (!formRef.current) return;
    
    const formData = new FormData(formRef.current);
    const businessName = formData.get('businessName');
    const contactName = formData.get('contactName');
    const contactEmail = formData.get('contactEmail');
    
    if(!businessName||!contactName||!contactEmail) {
      return setErr("Business, Contact name & Email are required.");
    }
    
    const data = {
      projectType: formData.get('projectType'),
      businessName: businessName,
      url: formData.get('url'),
      industry: formData.get('industry'),
      locations: formData.get('locations'),
      budget: formData.get('budget'),
      deadline: formData.get('deadline'),
      objectives: objectives,
      details: formData.get('details'),
      contact: {
        name: contactName,
        email: contactEmail,
        phone: formData.get('contactPhone')
      }
    };
    
    onSubmit(data);
  };
  
  const Field=({label,children,icon})=> (
    <div className="relative">
      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        {icon && <span className="text-[#7350FF]">{icon}</span>}
        {label}
      </label>
      {children}
    </div>
  );
  
  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#12C7FF]/5 py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-sm font-medium mb-4">
            <span>🚀</span>
            Project Requirements
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tell Us About Your
            <span className="block bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] bg-clip-text text-transparent">
              Dream Project
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Share your vision with us and let's create something extraordinary together. We'll analyze your requirements and provide a tailored solution.
          </p>
        </div>

        {/* Error Message */}
        {err && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <div className="font-semibold">Please fix the following:</div>
              <div className="text-sm">{err}</div>
            </div>
          </div>
        )}

        {/* Requirements Form */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] text-white p-8 text-center">
            <div className="text-4xl mb-4">📋</div>
            <h2 className="text-2xl font-bold mb-2">Project Requirements Form</h2>
            <p className="opacity-90">Fill in the details below and we'll get back to you with a customized proposal</p>
          </div>

          {/* Form Content */}
          <form ref={formRef} className="p-8 space-y-8">
            {/* Project Details Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-[#7350FF]">🎯</span>
                Project Details
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Project Type" icon="🏗️">
            <select 
                    name="projectType"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    defaultValue={PROJECT_TYPES[0]}
            >
              {PROJECT_TYPES.map(t=>(<option key={t}>{t}</option>))}
            </select>
          </Field>
                <Field label="Business Name" icon="🏢">
            <input 
                    name="businessName"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="Your business name"
            />
          </Field>
                <Field label="Current Website (optional)" icon="🌐">
            <input 
                    name="url"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="https://yourwebsite.com"
            />
          </Field>
                <Field label="Industry" icon="🏭">
            <input 
                    name="industry"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="Healthcare, SaaS, E-commerce..."
            />
          </Field>
                <Field label="Target Locations" icon="📍">
            <input 
                    name="locations"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="Mumbai, Delhi, Pan India..."
            />
          </Field>
                <Field label="Budget Range" icon="💰">
            <select 
                    name="budget"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
            >
              {['< ₹25k','₹25k–₹75k','₹75k–₹2L','₹2L+'].map(b=>(<option key={b}>{b}</option>))}
            </select>
          </Field>
                <Field label="Desired Launch Date" icon="📅">
            <input 
                    name="deadline"
              type="date" 
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
            />
          </Field>
              </div>
            </div>

            {/* Objectives Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-indigo-600">🎯</span>
                Project Objectives
              </h3>
              <div className="flex flex-wrap gap-3">
              {["Leads","Sales","Traffic","Branding","Appointments"].map(o=> (
                  <label key={o} className={`px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200 font-medium ${
                    objectives.includes(o)
                      ? 'bg-indigo-100 border-indigo-500 text-indigo-700 shadow-lg'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:bg-indigo-50'
                  }`}>
                  <input 
                    type="checkbox" 
                    className="hidden" 
                      checked={objectives.includes(o)} 
                    onChange={() => toggle(o)}
                  /> 
                  {o}
              </label>
              ))}
          </div>
          </div>

            {/* Detailed Requirements */}
            <div className="space-y-4">
              <Field label="Detailed Requirements" icon="📝">
            <textarea 
                  name="details"
                  rows={6}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white resize-none" 
                  placeholder="Describe your project goals, specific features needed, integrations, target audience, and any other important details..."
            />
          </Field>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-indigo-600">👤</span>
                Contact Information
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="Contact Name" icon="👤">
            <input 
                    name="contactName"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="Your full name"
            />
          </Field>
                <Field label="Contact Email" icon="📧">
            <input 
                    name="contactEmail"
                    type="email"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
              placeholder="you@example.com"
            />
          </Field>
                <Field label="Contact Phone" icon="📱">
            <input 
                    name="contactPhone"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 bg-gray-50 focus:bg-white" 
                    placeholder="+91 98765 43210"
            />
          </Field>
        </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button 
                type="button"
                onClick={submit} 
                className="flex-1 px-8 py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6]"
              >
                <span className="flex items-center justify-center gap-2">
                  <span>🚀</span>
                  Submit Requirements
                </span>
              </button>
              <button 
                type="button"
                onClick={onCancel} 
                className="px-8 py-4 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-2">
                  <span>❌</span>
                  Cancel
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export function MyReqs({items}){
  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      <h2 className="text-3xl font-bold mb-6">My Requests</h2>
      <div className="grid gap-4">
        {items.length===0 && <div className="text-gray-600">No requests yet.</div>}
        {items.map(r=> (
          <div key={r.id} className="rounded-2xl bg-white border shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold">{r.projectType} — {r.businessName}</div>
              <span className="text-xs px-2 py-1 rounded-full border bg-gray-50">{r.status}</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">Created: {fmt(r.createdAt)}</div>
            {r.url && <div className="text-sm text-gray-700">Website: {r.url}</div>}
            <div className="text-sm text-gray-700 mt-2">Objectives: {(r.objectives||[]).join(', ')||'—'}</div>
            <div className="text-sm text-gray-700 mt-2">Details: {r.details||'—'}</div>
          </div>
        ))}
      </div>
    </main>
  );
}

export function Admin({items,setItems,users,setUsers}){
  const [filter,setFilter]=useState("All");
  const [serviceFilter,setServiceFilter]=useState("All");
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  const [itemToDelete,setItemToDelete]=useState(null);
  const [deleteType,setDeleteType]=useState(''); // 'requirement' or 'user'
  
  const list=items.filter(r=>{
    const statusMatch = filter==="All" || r.status===filter;
    const serviceMatch = serviceFilter==="All" || 
      (serviceFilter==="Custom Webdesigning" && r.serviceType==="Custom Webdesigning") ||
      (serviceFilter==="Regular Requirements" && r.serviceType!=="Custom Webdesigning");
    return statusMatch && serviceMatch;
  });
  
  const update=(id,status)=>setItems(items.map(r=>r.id===id?{...r,status}:r));
  
  // Delete functions
  const deleteRequirement=(id)=>{
    setItems(items.filter(r=>r.id!==id));
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };
  
  const deleteUser=(email)=>{
    setUsers(users.filter(u=>u.email!==email));
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };
  
  const confirmDelete=(item,type)=>{
    setItemToDelete(item);
    setDeleteType(type);
    setShowDeleteConfirm(true);
  };
  
  // Admin management functions
  const createAdmin=()=>{
    const adminUser={
      email:'omatechdigital@gmail.com',
      name:'Omatech Digital Admin',
      role:'admin',
      password:'admin123', // Default password for admin
      phoneVerified:true,
      createdAt:Date.now()
    };
    setUsers(prev=>[adminUser,...prev]);
    alert('✅ Admin user omatechdigital@gmail.com created successfully!\n\n🔑 Login Credentials:\nEmail: omatechdigital@gmail.com\nPassword: admin123\n\n⚠️ Please change this password after first login!');
  };
  
  const checkUsers=()=>{
    console.log('Current users:', users);
    alert(`📊 Total users: ${users.length}\n👑 Admins: ${users.filter(u=>u.role==='admin').length}\n👥 Regular users: ${users.filter(u=>u.role!=='admin').length}`);
  };
  
  const changeAdminPassword=()=>{
    const newPassword = prompt('Enter new password for omatechdigital@gmail.com (min 6 characters):');
    if (newPassword && newPassword.length >= 6) {
      setUsers(prev => prev.map(u => 
        u.email === 'omatechdigital@gmail.com' 
          ? {...u, password: newPassword}
          : u
      ));
      alert('✅ Admin password updated successfully!');
    } else if (newPassword !== null) {
      alert('❌ Password must be at least 6 characters long!');
    }
  };
  
  const checkAdminStatus=()=>{
    const admin = users.find(u => u.email === 'omatechdigital@gmail.com');
    if (admin) {
      alert(`👑 Admin Status:\nEmail: ${admin.email}\nName: ${admin.name}\nRole: ${admin.role}\nPassword: ${admin.password ? 'Set' : 'Not set'}\nCreated: ${new Date(admin.createdAt).toLocaleString()}`);
    } else {
      alert('❌ Admin user omatechdigital@gmail.com does not exist.\nClick "Create Admin" to create it.');
    }
  };
  
  const removeAdmin=()=>{
    if (confirm('⚠️ Are you sure you want to remove the admin user omatechdigital@gmail.com?\n\nThis will remove admin privileges from this account.')) {
      setUsers(prev => prev.map(u => 
        u.email === 'omatechdigital@gmail.com' 
          ? {...u, role: 'user'}
          : u
      ));
      alert('✅ Admin privileges removed from omatechdigital@gmail.com');
    }
  };
  
  return (
    <main className="max-w-7xl mx-auto px-4 py-12">
      {/* Admin Management Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200">
        <h3 className="text-xl font-semibold text-blue-800 mb-4">👑 Admin Management</h3>
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={createAdmin}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            👑 Create Admin (omatechdigital@gmail.com)
          </button>
          <button 
            onClick={changeAdminPassword}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            🔑 Change Admin Password
          </button>
          <button 
            onClick={checkAdminStatus}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            🔍 Check Admin Status
          </button>
          <button 
            onClick={removeAdmin}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            🚫 Remove Admin
          </button>
          <button 
            onClick={checkUsers}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            👥 Check Users
          </button>
        </div>
        <p className="text-sm text-[#7350FF] mt-3">
          Use these tools to manage admin access. The admin user will have full privileges.
        </p>
      </div>
      
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-3xl font-bold">Admin Panel</h2>
        <div className="flex gap-3">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="border rounded-lg px-3 py-2">
            {["All",...STATUSES].map(s=>(<option key={s}>{s}</option>))}
          </select>
          <select 
            value={serviceFilter}
            onChange={e => setServiceFilter(e.target.value)} 
            className="border rounded-lg px-3 py-2"
          >
            <option value="All">All Services</option>
            <option value="Custom Webdesigning">🎨 Custom Webdesigning</option>
            <option value="Regular Requirements">📋 Regular Requirements</option>
          </select>
        </div>
      </div>
      <div className="grid gap-4">
        {list.length===0 && <div className="text-gray-600">No records.</div>}
        {list.map(r=> (
          <div key={r.id} className="rounded-2xl bg-white border shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold">
                {r.serviceType === 'Custom Webdesigning' ? '🎨 Custom Website Design' : r.projectType} — {r.businessName}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full border bg-gray-50">{r.status}</span>
                <select value={r.status} onChange={e => update(r.id, e.target.value)} className="border rounded-lg px-2 py-2 text-sm">{STATUSES.map(s=>(<option key={s}>{s}</option>))}</select>
                <button 
                  onClick={()=>confirmDelete(r,'requirement')}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  title="Delete this requirement"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-600 mt-1">Submitted: {fmt(r.createdAt)}</div>
            
            {/* Custom Webdesigning Service Details */}
            {r.serviceType === 'Custom Webdesigning' && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-3">🎨 Custom Website Requirements</h4>
                
                {/* Drawing Preview */}
                {r.drawing && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-purple-700 mb-2">Website Sketch:</h5>
                    <img 
                      src={r.drawing} 
                      alt="Website sketch" 
                      className="max-w-full h-auto rounded border-2 border-purple-300 max-h-48"
                    />
                  </div>
                )}
                
                {/* Requirements Details */}
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-purple-700">Website Type:</span> {r.requirements?.websiteType}
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">Pages:</span> {r.requirements?.pageCount}
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">Design Style:</span> {r.requirements?.designStyle}
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">Pricing:</span> {r.pricing}
                  </div>
                </div>
                
                {/* Special Features */}
                {r.requirements?.specialFeatures && (
                  <div className="mt-3">
                    <span className="font-medium text-purple-700">Special Features:</span>
                    <p className="text-gray-700 mt-1">{r.requirements.specialFeatures}</p>
                  </div>
                )}
                
                {/* Additional Notes */}
                {r.requirements?.additionalNotes && (
                  <div className="mt-3">
                    <span className="font-medium text-purple-700">Additional Notes:</span>
                    <p className="text-gray-700 mt-1">{r.requirements.additionalNotes}</p>
                  </div>
                )}
                
                {/* User Info */}
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <span className="font-medium text-purple-700">Requested by:</span> {r.userEmail}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-10">
        <h3 className="text-xl font-semibold mb-3">Users</h3>
        <div className="rounded-2xl bg-white border shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Phone</th><th className="text-left px-4 py-2">Verified</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Actions</th></tr></thead>
            <tbody>
              {users.map((u,i)=>(
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2">{u.name||'—'}</td>
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2">{u.phone||'—'}</td>
                    <td className="px-4 py-2">{u.phoneVerified? 'Yes':'No'}</td>
                    <td className="px-4 py-2">{u.role||'user'}</td>
                    <td className="px-4 py-2">
                      <button 
                        onClick={()=>confirmDelete(u,'user')}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        title="Delete this user"
                        disabled={u.role === 'admin'}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold mb-2">
                Confirm Delete
              </h3>
              <p className="text-gray-600 mb-6">
                {deleteType === 'requirement' 
                  ? `Are you sure you want to delete this ${itemToDelete.serviceType === 'Custom Webdesigning' ? 'custom website request' : 'requirement'}?`
                  : `Are you sure you want to delete user "${itemToDelete.email}"?`
                }
                <br />
                <span className="text-red-600 font-medium">This action cannot be undone!</span>
              </p>
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={()=>setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={()=>deleteType === 'requirement' ? deleteRequirement(itemToDelete.id) : deleteUser(itemToDelete.email)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export const Denied=()=> (
  <div className="max-w-xl mx-auto px-4 py-16 text-center">
    <div className="text-4xl">⛔</div>
    <h3 className="text-xl font-semibold mt-2">Access denied</h3>
    <p className="text-gray-600 mt-1">You need admin privileges to view this page.</p>
  </div>
);

// Shared small Field helper used by many screens
export const Field=({label,children})=> (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    {children}
  </div>
);

// Stat component for displaying statistics
export const Stat=({label,value})=> (
  <div className="p-4 rounded-xl bg-white border shadow-sm">
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

// Backup Management Component
export function BackupManager({ setRoute }) {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [action, setAction] = useState(null);
  const [autoBackup, setAutoBackup] = useState(true);

  // Load backups when component mounts
  React.useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = () => {
    try {
      const storedBackups = localStorage.getItem('omatech_website_backups');
      if (storedBackups) {
        const parsedBackups = JSON.parse(storedBackups);
        setBackups(parsedBackups);
        
        // Calculate stats
        const totalSize = JSON.stringify(parsedBackups).length;
        setStats({
          totalBackups: parsedBackups.length,
          totalSize: formatBytes(totalSize),
          oldestBackup: parsedBackups.length > 0 ? formatTimestamp(parsedBackups[parsedBackups.length - 1].timestamp) : 'None',
          newestBackup: parsedBackups.length > 0 ? formatTimestamp(parsedBackups[0].timestamp) : 'None'
        });
      }
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const createBackup = () => {
    const description = prompt('Enter backup description:');
    if (description) {
      const newBackup = {
        id: `backup_${Date.now()}`,
        timestamp: Date.now(),
        date: new Date().toISOString(),
        description,
        source: 'user',
        files: { 'current_state': 'Website current state' }
      };

      const updatedBackups = [newBackup, ...backups];
      localStorage.setItem('omatech_website_backups', JSON.stringify(updatedBackups));
      setBackups(updatedBackups);
      loadBackups();
      
      // Show success notification
      showNotification(`Backup "${description}" created successfully!`, 'success');
    }
  };

  const restoreBackup = (backupId) => {
    setSelectedBackup(backups.find(b => b.id === backupId));
    setAction('restore');
    setShowConfirm(true);
  };

  const deleteBackup = (backupId) => {
    setSelectedBackup(backups.find(b => b.id === backupId));
    setAction('delete');
    setShowConfirm(true);
  };

  const confirmAction = () => {
    if (action === 'restore' && selectedBackup) {
      // Create safety backup before restoring
      const safetyBackup = {
        id: `backup_${Date.now()}`,
        timestamp: Date.now(),
        date: new Date().toISOString(),
        description: `Safety backup before restoring: ${selectedBackup.description}`,
        source: 'system',
        files: { 'safety_state': 'Current state before restoration' }
      };

      const updatedBackups = [safetyBackup, ...backups];
      localStorage.setItem('omatech_website_backups', JSON.stringify(updatedBackups));
      
      // Show restoration message
      showNotification(`Website restored to: ${selectedBackup.description}`, 'success');
      
      // Simulate restoration (reload page)
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else if (action === 'delete' && selectedBackup) {
      const updatedBackups = backups.filter(b => b.id !== selectedBackup.id);
      localStorage.setItem('omatech_website_backups', JSON.stringify(updatedBackups));
      setBackups(updatedBackups);
      loadBackups();
      showNotification(`Backup "${selectedBackup.description}" deleted`, 'info');
    }
    
    setShowConfirm(false);
    setSelectedBackup(null);
    setAction(null);
  };

  const clearAllBackups = () => {
    if (confirm('Are you sure you want to delete ALL backups? This action cannot be undone.')) {
      localStorage.removeItem('omatech_website_backups');
      setBackups([]);
      loadBackups();
      showNotification('All backups cleared', 'info');
    }
  };

  const exportBackups = () => {
    const dataStr = JSON.stringify(backups, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `omatech-backups-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    showNotification('Backups exported successfully!', 'success');
  };

  const importBackups = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedBackups = JSON.parse(e.target.result);
          const mergedBackups = [...importedBackups, ...backups];
          
          // Remove duplicates and keep only latest 50
          const uniqueBackups = mergedBackups
            .filter((backup, index, self) => index === self.findIndex(b => b.id === backup.id))
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 50);
          
          localStorage.setItem('omatech_website_backups', JSON.stringify(uniqueBackups));
          setBackups(uniqueBackups);
          loadBackups();
          
          showNotification(`${importedBackups.length} backups imported successfully!`, 'success');
        } catch (error) {
          showNotification('Failed to import backups: Invalid file format', 'error');
        }
      };
      reader.readAsText(file);
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
      type === 'success' ? 'bg-green-500 text-white' :
      type === 'error' ? 'bg-red-500 text-white' :
      'bg-blue-500 text-white'
    }`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Website Backup Manager</h1>
            <button 
              onClick={() => setRoute('home')}
              className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
            >
              ← Back to Website
            </button>
          </div>
          <p className="text-gray-600">Manage automatic snapshots and restore your website to any previous version</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="text-2xl font-bold text-indigo-600">{stats.totalBackups || 0}</div>
            <div className="text-sm text-gray-600">Total Backups</div>
          </div>
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="text-2xl font-bold text-green-600">{stats.totalSize || '0 Bytes'}</div>
            <div className="text-sm text-gray-600">Total Size</div>
          </div>
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="text-sm font-medium text-gray-900">{stats.newestBackup || 'None'}</div>
            <div className="text-sm text-gray-600">Latest Backup</div>
          </div>
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="text-sm font-medium text-gray-900">{stats.oldestBackup || 'None'}</div>
            <div className="text-sm text-gray-600">Oldest Backup</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white p-6 rounded-lg border shadow-sm mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            <button 
              onClick={createBackup}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              💾 Create Backup
            </button>
            <button 
              onClick={exportBackups}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              📤 Export All
            </button>
            <label className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition cursor-pointer">
              📥 Import Backups
              <input 
                type="file" 
                accept=".json" 
                onChange={importBackups} 
                className="hidden"
              />
            </label>
            <button 
              onClick={clearAllBackups}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              🗑️ Clear All
            </button>
          </div>
        </div>

        {/* Backups List */}
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold">Backup History</h2>
            <p className="text-sm text-gray-600 mt-1">Click on a backup to restore or delete it</p>
          </div>
          
          {backups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-4">💾</div>
              <p>No backups found. Create your first backup to get started!</p>
            </div>
          ) : (
            <div className="divide-y">
              {backups.map((backup) => (
                <div key={backup.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-semibold">{backup.description}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          backup.source === 'user' ? 'bg-blue-100 text-blue-800' :
                          backup.source === 'system' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {backup.source}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Created: {formatTimestamp(backup.timestamp)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => restoreBackup(backup.id)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm"
                      >
                        🔄 Restore
                      </button>
                      <button 
                        onClick={() => deleteBackup(backup.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">
                {action === 'restore' ? 'Restore Website?' : 'Delete Backup?'}
              </h3>
              <p className="text-gray-600 mb-6">
                {action === 'restore' 
                  ? `Are you sure you want to restore the website to "${selectedBackup?.description}"? This will create a safety backup first.`
                  : `Are you sure you want to delete the backup "${selectedBackup?.description}"? This action cannot be undone.`
                }
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={confirmAction}
                  className={`flex-1 py-2 rounded-lg text-white transition ${
                    action === 'restore' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {action === 'restore' ? 'Yes, Restore' : 'Yes, Delete'}
                </button>
                <button 
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Cart({ cart, removeFromCart, clearCart, getCartTotal, setRoute, session }) {
  const { subtotal, gst, total } = getCartTotal();

  const buildInvoiceHtml = () => {
    const rows = cart.map((item, idx) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;">${idx+1}.</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">${item.name}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">${item.description || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eee;text-align:right;color:#666;">Quote on request</td>
      </tr>
    `).join('');

    return `
      <div style="font-family:Arial,sans-serif;color:#333">
        <h2 style="margin:0 0 10px 0">Omatech Digital - Request for Quote</h2>
        <p style="margin:0 0 16px 0;color:#666">Thank you for your interest. Our team will contact you with a custom quote and next steps.</p>
        <table style="width:100%;border-collapse:collapse;background:#fff;border:1px solid #eee">
          <thead>
            <tr style="background:#f8fafc">
              <th style="padding:8px;text-align:left;border-bottom:1px solid #eee">#</th>
              <th style="padding:8px;text-align:left;border-bottom:1px solid #eee">Service</th>
              <th style="padding:8px;text-align:left;border-bottom:1px solid #eee">Details</th>
              <th style="padding:8px;text-align:right;border-bottom:1px solid #eee">Quote</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <p style="margin:16px 0 0 0;color:#666;font-size:14px">We will send you a custom quote and arrange a call to discuss your project.</p>
      </div>
    `;
  };

  const handleCheckout = async () => {
    try {
      const email = session?.email;
      const invoiceHtml = buildInvoiceHtml();

      if (email) {
        await fetch('/.netlify/functions/user-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notificationType: 'order_invoice',
            userData: {
              email,
              name: email.split('@')[0],
              items: cart,
              subtotal,
              gst,
              total,
              html: invoiceHtml
            }
          })
        }).catch(()=>{});
      }

      // Navigate to checkout notice page (no dialog)
      setRoute('checkout');
    } catch (e) {
      console.error('Checkout error', e);
      setRoute('checkout');
    }
  };

  if (cart.length === 0) {
    return (
      <main className="relative min-h-screen bg-gradient-to-br from-[#020617] via-[#111827] to-[#020617] py-12 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 border border-[#8B00FF]/20 rounded-full animate-pulse"></div>
          <div className="absolute top-20 right-16 w-24 h-24 border border-[#12C7FF]/20 rotate-45"></div>
          <div className="absolute bottom-20 left-16 w-20 h-20 border border-[#8B00FF]/20 rotate-12"></div>
          <div className="absolute bottom-16 right-20 w-28 h-28 border border-[#12C7FF]/20 rounded-full"></div>
          
          {/* Animated Lines */}
          <div className="absolute top-1/4 left-0 w-48 h-px bg-gradient-to-r from-transparent via-[#8B00FF]/30 to-transparent"></div>
          <div className="absolute bottom-1/4 right-0 w-48 h-px bg-gradient-to-l from-transparent via-[#12C7FF]/30 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="text-8xl mb-8 animate-bounce">🛒</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Your Cart is Empty</h1>
            <p className="text-xl text-white/80 mb-10">Add some amazing services to get started!</p>
            <button 
              onClick={() => setRoute('pricing')}
              className="px-8 py-4 bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] text-white rounded-2xl font-semibold text-lg hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/40"
            >
              Browse Services
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen bg-gradient-to-br from-[#020617] via-[#111827] to-[#020617] py-12 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 border border-[#8B00FF]/20 rounded-full animate-pulse"></div>
        <div className="absolute top-20 right-16 w-24 h-24 border border-[#12C7FF]/20 rotate-45"></div>
        <div className="absolute bottom-20 left-16 w-20 h-20 border border-[#8B00FF]/20 rotate-12"></div>
        <div className="absolute bottom-16 right-20 w-28 h-28 border border-[#12C7FF]/20 rounded-full"></div>
        
        {/* Animated Lines */}
        <div className="absolute top-1/4 left-0 w-48 h-px bg-gradient-to-r from-transparent via-[#8B00FF]/30 to-transparent"></div>
        <div className="absolute bottom-1/4 right-0 w-48 h-px bg-gradient-to-l from-transparent via-[#12C7FF]/30 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Shopping Cart</h1>
            <p className="text-white/80">{cart.length} item{cart.length !== 1 ? 's' : ''} in your cart</p>
          </div>
          <button 
            onClick={clearCart}
            className="px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-xl transition-all duration-300 border border-red-400/30"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-500/25 border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/20">
                <h2 className="text-xl font-semibold text-white">Cart Items</h2>
              </div>
              <div className="divide-y divide-white/20">
                {cart.map((item) => (
                  <div key={item.id} className="p-6 flex items-center gap-4 hover:bg-white/5 transition-all duration-300">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{item.name}</h3>
                      <p className="text-white/80 mb-2">{item.description}</p>
                      <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>Added: {new Date(item.addedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/70 mb-2">Quote on request</div>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 border border-red-400/30"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-500/25 border border-white/20 p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-white mb-6">Order Summary</h2>
              
              <div className="mb-6">
                <p className="text-white/80 text-sm">Custom quote will be sent after you submit. We’ll contact you with pricing and next steps.</p>
                <p className="text-white/60 text-sm mt-2">{cart.length} item{cart.length !== 1 ? 's' : ''} selected</p>
              </div>

              <button 
                onClick={handleCheckout}
                className="w-full py-4 bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] text-white rounded-xl font-semibold text-lg hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/40 mb-4"
              >
                Proceed to Checkout
              </button>
              
              <button 
                onClick={() => setRoute('pricing')}
                className="w-full py-3 border-2 border-white/30 text-white/80 rounded-xl font-semibold hover:bg-white/10 hover:text-white transition-all duration-300"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export function CheckoutNotice({session,setRoute}){
  const isLoggedIn = !!session?.email;
  return (
    <main className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-12 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 border border-purple-400/20 rounded-full animate-pulse"></div>
        <div className="absolute top-20 right-16 w-24 h-24 border border-cyan-400/20 rotate-45"></div>
        <div className="absolute bottom-20 left-16 w-20 h-20 border border-pink-400/20 rotate-12"></div>
        <div className="absolute bottom-16 right-20 w-28 h-28 border border-green-400/20 rounded-full"></div>
        
        {/* Animated Lines */}
        <div className="absolute top-1/4 left-0 w-48 h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent"></div>
        <div className="absolute bottom-1/4 right-0 w-48 h-px bg-gradient-to-l from-transparent via-cyan-400/30 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-500/25 border border-white/20 p-8 text-center">
          <div className="text-6xl mb-6 animate-bounce">🧾</div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {isLoggedIn ? 'Order Received' : 'Sign in to Continue'}
          </h1>
          {isLoggedIn ? (
            <>
              <p className="text-white/80 mb-8 text-lg">
                Thank you for choosing Omatech Digital. A professional proforma invoice has been sent to
                <span className="font-semibold text-purple-300"> {session.email}</span>. Our team will contact you shortly to arrange payment and begin your project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={()=>setRoute('dashboard')} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/40">Go to Dashboard</button>
                <button onClick={()=>setRoute('services')} className="px-6 py-3 border-2 border-white/30 text-white/80 rounded-xl font-semibold hover:bg-white/10 hover:text-white transition-all duration-300">Explore Services</button>
                <button onClick={()=>setRoute('home')} className="px-6 py-3 border-2 border-white/30 text-white/80 rounded-xl font-semibold hover:bg-white/10 hover:text-white transition-all duration-300">Back to Home</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-white/80 mb-8 text-lg">
                Only registered users can proceed to checkout and receive an invoice. Please sign in or create an account to complete your order.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={()=>setRoute('login')} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/40">Login to Continue</button>
                <button onClick={()=>setRoute('register')} className="px-6 py-3 border-2 border-white/30 text-white/80 rounded-xl font-semibold hover:bg-white/10 hover:text-white transition-all duration-300">Create Account</button>
                <button onClick={()=>setRoute('cart')} className="px-6 py-3 border-2 border-white/30 text-white/80 rounded-xl font-semibold hover:bg-white/10 hover:text-white transition-all duration-300">Back to Cart</button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

